class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener("keyup", (evt) => evt.code === "Escape" && this.close());
    this.querySelector("#CartDrawer-Overlay").addEventListener("click", this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector("#cart-icon-bubble");
    cartLink.setAttribute("role", "button");
    cartLink.setAttribute("aria-haspopup", "dialog");
    cartLink.addEventListener("click", (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener("keydown", (event) => {
      if (event.code.toUpperCase() === "SPACE") {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute("role")) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add("animate", "active");
    });

    this.addEventListener(
      "transitionend",
      () => {
        const containerToTrapFocusOn = document.getElementById("CartDrawer");
        const focusElement = this.querySelector(".drawer__inner") || this.querySelector(".drawer__close");
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    if (scrollbarWidth > 0) {
      document.documentElement.style.setProperty('--scrollbar-compensation', `${scrollbarWidth}px`);
    }
    document.documentElement.classList.add("overflow-hidden");
    document.body.classList.add("overflow-hidden");
  }

  close() {
    this.classList.remove("active");
    removeTrapFocus(this.activeElement);
    document.documentElement.classList.remove("overflow-hidden");
    document.body.classList.remove("overflow-hidden");
    document.documentElement.style.removeProperty('--scrollbar-compensation');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute("role", "button");
    cartDrawerNote.setAttribute("aria-expanded", "false");

    if (cartDrawerNote.nextElementSibling.getAttribute("id")) {
      cartDrawerNote.setAttribute("aria-controls", cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener("click", (event) => {
      event.currentTarget.setAttribute("aria-expanded", !event.currentTarget.closest("details").hasAttribute("open"));
    });

    cartDrawerNote.parentElement.addEventListener("keyup", onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector(".drawer__inner").classList.contains("is-empty") && this.querySelector(".drawer__inner").classList.remove("is-empty");
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    // /cart/add.js response `items` only contains the *added* item(s), not all cart
    // items. Always fetch /cart.js so upsell logic sees the complete cart.
    const afterRender = (cart) => {
      syncCartDependentUpsells(cart);
      if (typeof updateVerraKidsGiftMessage === 'function') updateVerraKidsGiftMessage(cart);
    };
    fetch('/cart.js').then(r => r.json()).then(afterRender).catch(() => {});

    setTimeout(() => {
      this.querySelector("#CartDrawer-Overlay").addEventListener("click", this.close.bind(this));
      this.open();
      if (window.initUpsellCarousel) window.initUpsellCarousel();
    });
  }

  getSectionInnerHTML(html, selector = ".shopify-section") {
    return new DOMParser().parseFromString(html, "text/html").querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: "cart-drawer",
        selector: "#CartDrawer",
      },
      {
        id: "cart-icon-bubble",
      },
    ];
  }

  getSectionDOM(html, selector = ".shopify-section") {
    return new DOMParser().parseFromString(html, "text/html").querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      }
    ];
  }

  // Override to update the cart drawer silently:
  // - no loading overlay, no disabled state
  // - surgical DOM patch for quantity changes (no flash)
  // - full re-render only when an item is removed (line indices shift)
  updateQuantity(line, quantity, name) {
    const body = JSON.stringify({
      line,
      quantity,
      sections: ['cart-drawer', 'cart-icon-bubble'],
      sections_url: window.location.pathname,
    });

    fetch(routes.cart_change_url, { ...fetchConfig(), ...{ body } })
      .then(r => r.text())
      .then(state => {
        const parsedState = JSON.parse(state);

        // Always update the header cart icon bubble
        const iconBubbleEl = document.getElementById('cart-icon-bubble');
        if (iconBubbleEl && parsedState.sections['cart-icon-bubble']) {
          const newBubble = new DOMParser()
            .parseFromString(parsedState.sections['cart-icon-bubble'], 'text/html')
            .querySelector('.shopify-section');
          if (newBubble) iconBubbleEl.innerHTML = newBubble.innerHTML;
        }

        const drawerInner = document.getElementById('CartDrawer')?.querySelector('.drawer__inner');
        const currentLineCount = drawerInner ? drawerInner.querySelectorAll('.cart-item[id^="CartDrawer-Item-"]').length : 0;
        const newDoc = new DOMParser()
          .parseFromString(parsedState.sections['cart-drawer'], 'text/html');
        const newLineCount = newDoc.querySelectorAll('.cart-item[id^="CartDrawer-Item-"]').length;
        const lineCountChanged = currentLineCount !== newLineCount;

        if (parsedState.item_count === 0 || parseInt(quantity) === 0 || lineCountChanged) {
          document.querySelector('cart-drawer')?.classList.toggle('is-empty', parsedState.item_count === 0);
          this.classList.toggle('is-empty', parsedState.item_count === 0);
          if (drawerInner && parsedState.sections['cart-drawer']) {
            const newInner = newDoc.querySelector('.drawer__inner');
            if (newInner) drawerInner.innerHTML = newInner.innerHTML;
          }
          setTimeout(() => {
            if (window.initUpsellCarousel) window.initUpsellCarousel();
          }, 50);
        } else {
          const itemEl = document.getElementById(`CartDrawer-Item-${line}`);
          const newItemEl = newDoc.getElementById(`CartDrawer-Item-${line}`);
          if (itemEl && newItemEl) {
            const details = itemEl.querySelector('.cart-item__details');
            const newDetails = newItemEl.querySelector('.cart-item__details');
            if (details && newDetails) details.innerHTML = newDetails.innerHTML;
          }

          const heading = document.querySelector('#CartDrawer .drawer__heading');
          const newHeading = newDoc.querySelector('.drawer__heading');
          if (heading && newHeading) heading.innerHTML = newHeading.innerHTML;

          const footer = document.querySelector('#CartDrawer .cart-drawer__footer');
          const newFooter = newDoc.querySelector('.cart-drawer__footer');
          if (footer && newFooter) footer.innerHTML = newFooter.innerHTML;

          setTimeout(() => { if (window.initUpsellCarousel) window.initUpsellCarousel(); }, 50);
        }

        // Update progress bar, upsells, and gifts immediately (synchronous)
        syncCartDependentUpsells(parsedState);
        if (typeof updateVerraKidsGiftMessage === 'function') {
          updateVerraKidsGiftMessage(parsedState);
        }

        // Dispatch cart:updated for any other listeners (mark as already synced)
        parsedState._upsellsSynced = true;
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: parsedState }));

        if (window.initCartCountdownTimers) {
          setTimeout(() => window.initCartCountdownTimers(), 200);
        }
      })
      .catch(err => {
        console.error(err);
        const errors = document.getElementById('CartDrawer-CartErrors');
        if (errors) errors.textContent = window.cartStrings?.error || '';
      });
  }
}

function updateFreeShippingBar(cartTotalPrice, cartCurrency) {
  const barContainers = document.querySelectorAll('[data-cart-progress-bar]');
  if (!barContainers.length) return;

  // Use settings from window.cartShippingSettings (passed from Liquid)
  const settings = window.cartShippingSettings;
  
  if (!settings) return;

  // Determine currency: prefer cart API response, then meta tag, then data-currency on bar, then CZK (multi-currency fix)
  const currencyFromBar = barContainers[0]?.dataset?.currency;
  const currencyRaw = (cartCurrency && typeof cartCurrency === 'string' ? cartCurrency.trim() : null)
    || document.querySelector('meta[name="currency"]')?.content?.trim()
    || currencyFromBar?.trim()
    || 'CZK';
  const currencyCode = currencyRaw.toUpperCase();
  // Threshold logic only supports CZK and EUR; treat any other currency as CZK
  const currency = currencyCode === 'EUR' ? 'EUR' : 'CZK';
  const locale =
    document.documentElement.lang?.replace('_', '-')
    || navigator.language
    || 'cs-CZ';
  const formatCurrency = (amountInCents) => {
    const normalised = Math.max(amountInCents, 0) / 100;
    const fractionOpts = currency === 'CZK'
      ? { minimumFractionDigits: 0, maximumFractionDigits: 0 }
      : {};
    return normalised.toLocaleString(locale, {
      style: 'currency',
      currency: currencyCode,
      ...fractionOpts,
    });
  };

  const limit = currency === 'EUR'
    ? settings.freeShippingThresholdEUR
    : settings.freeShippingThresholdCZK;

  // Free Bag: only enable when setting is on and (if custom dates) within date range
  let bagLimit = null;
  if (settings.enableFreeBagGift) {
    const bagDateValid = settings.enableFreeBagCustomDate
      ? isDateValid(settings.freeBagValidFrom, settings.freeBagValidUntil, 'Free Bag')
      : true;
    if (bagDateValid) {
      bagLimit = currency === 'EUR'
        ? settings.freeBagThresholdEUR
        : settings.freeBagThresholdCZK;
    }
  }

  // Check Verra BOX date validity before enabling
  let boxLimit = null;
  if (settings.enableVerraBoxGift) {
    const boxDateValid = settings.enableVerraBoxCustomDate
      ? isDateValid(settings.verraBoxValidFrom, settings.verraBoxValidUntil, 'Verra BOX')
      : true;
    if (boxDateValid) {
      boxLimit = currency === 'EUR'
        ? (settings.freeBoxThresholdEUR ?? 8000)
        : (settings.freeBoxThresholdCZK ?? 200000);
    }
  }

  if (!limit) {
    return;
  }

  // Max progress: bag if enabled, otherwise shipping
  const maxProgress = bagLimit || limit;
  const shippingRemaining = Math.max(limit - cartTotalPrice, 0);
  const bagRemaining = bagLimit ? Math.max(bagLimit - cartTotalPrice, 0) : 0;
  const width = Math.max(0, Math.min((cartTotalPrice / maxProgress) * 100, 100));
  const shippingMarkerPosition = Math.max(
    0,
    Math.min(Math.round(((limit / maxProgress) * 100) * 10) / 10, 100)
  );
  const shippingReached = cartTotalPrice >= limit;
  const bagReached = bagLimit ? cartTotalPrice >= bagLimit : false;
  const boxReached = boxLimit ? cartTotalPrice >= boxLimit : false;
  const boxMarkerPosition = boxLimit
    ? Math.max(0, Math.min(Math.round(((boxLimit / maxProgress) * 100) * 10) / 10, 100))
    : 0;
  const boxRemaining = boxLimit ? Math.max(boxLimit - cartTotalPrice, 0) : 0;

  const shippingMessage = shippingReached
    ? settings.freeShippingAchievedText
    : settings.freeShippingRemainingText.replace('{{ amount }}', formatCurrency(shippingRemaining));

  const bagMessage = bagLimit
    ? (bagReached
      ? settings.freeBagAchievedText
      : settings.freeBagRemainingText.replace('{{ amount }}', formatCurrency(bagRemaining)))
    : '';

  const boxMessage = boxLimit
    ? (boxReached
      ? 'Získáte Verra BOX ZDARMA!'
      : `Objednejte ještě za ${formatCurrency(boxRemaining)} a získáte Verra BOX ZDARMA!`)
    : '';

  // Update all progress bar containers by manipulating existing DOM instead of rebuilding HTML
  barContainers.forEach((container) => {
    // Update progress bar fill width
    const progressFill = container.querySelector('[data-progress-fill]');
    if (progressFill) {
      progressFill.style.width = `${width}%`;
    }

    // Update shipping threshold
    updateThresholdMessage(container, 'shipping', shippingMessage, shippingReached, '🚐', '🎉');
    updateMarkerPosition(container, 'shipping', shippingMarkerPosition, shippingReached);

    // Update bag threshold (only when Free Bag is enabled)
    if (bagLimit) {
      updateThresholdMessage(container, 'bag', bagMessage, bagReached, '🎁', '🎉');
      // Bag marker stays at 100%
    }

    // Update box threshold if enabled
    if (boxLimit) {
      updateThresholdMessage(container, 'box', boxMessage, boxReached, '📦', '🎉');
      updateMarkerPosition(container, 'box', boxMarkerPosition, boxReached);
    }
  });
}

function updateThresholdMessage(container, type, message, reached, defaultIcon, reachedIcon) {
  const messageDiv = container.querySelector(`[data-threshold="${type}"]`);
  if (!messageDiv) return;

  const messageTarget = messageDiv.querySelector('[data-message-target]');
  const iconTarget = messageDiv.querySelector('[data-icon-target]');

  if (messageTarget) messageTarget.innerHTML = message;
  if (iconTarget) iconTarget.textContent = reached ? reachedIcon : defaultIcon;

  messageDiv.dataset.reached = reached;

  if (type === 'shipping' || type === 'bag') {
    // Hide message once threshold is reached
    messageDiv.style.display = reached ? 'none' : '';
  } else if (reached) {
    messageDiv.classList.add('tw-bg-[#F17FB120]', 'tw-border', 'tw-border-[#F17FB1]');
    messageDiv.classList.remove('tw-bg-white/90', 'tw-border-[#e0e0e0]');
  } else {
    messageDiv.classList.remove('tw-bg-[#F17FB120]', 'tw-bg-white/90', 'tw-border', 'tw-border-[#F17FB1]', 'tw-border-[#e0e0e0]');
  }

  if (reached) {
    messageTarget?.classList.remove('tw-font-medium');
    messageTarget?.classList.add('tw-font-semibold');
  } else {
    messageTarget?.classList.remove('tw-font-semibold');
    messageTarget?.classList.add('tw-font-medium');
  }
}

function updateMarkerPosition(container, type, position, reached) {
  const marker = container.querySelector(`[data-marker="${type}"]`);
  const labelIcon = container.querySelector(`[data-label-icon="${type}"]`);

  if (marker) {
    marker.style.left = `${position}%`;
    marker.textContent = reached ? '🎉' : (type === 'box' ? '🎁' : type === 'shipping' ? '🚐' : '🎁');
    
    if (reached) {
      marker.classList.add('reached');
    } else {
      marker.classList.remove('reached');
    }
  }

  if (labelIcon) {
    labelIcon.style.left = `${position}%`;
    labelIcon.textContent = type === 'box' ? '🎁' : (type === 'shipping' ? '🚐' : '🎁');
  }
}

// Check if a feature is within its valid date range
function isDateValid(validFrom, validUntil, featureName = '') {
  if (!validFrom && !validUntil) {
    return true; // No date restrictions
  }

  const now = Date.now();

  // Check start date
  if (validFrom && validFrom !== '') {
    const validFromTime = new Date(validFrom).getTime();
    if (now < validFromTime) {
      return false;
    }
  }

  // Check end date
  if (validUntil && validUntil !== '') {
    const validUntilTime = new Date(validUntil).getTime();
    if (now > validUntilTime) {
      return false;
    }
  }

  return true;
}

function isValidCartItems(items) {
  return Array.isArray(items);
}

/** Cart AJAX may return `currency` as a string code or an object with `iso_code`. */
function normalizeCartCurrency(cartOrCurrency) {
  if (cartOrCurrency == null) return null;
  if (typeof cartOrCurrency === 'string' && cartOrCurrency.trim()) {
    return cartOrCurrency.trim();
  }
  const c = cartOrCurrency.currency != null ? cartOrCurrency.currency : cartOrCurrency;
  if (typeof c === 'string' && c.trim()) return c.trim();
  if (c && typeof c === 'object' && c.iso_code) return String(c.iso_code).trim();
  return null;
}

var promoCampaignFreeGiftSyncing = false;

// Tracks, per gift product, whether the shopper has already seen the auto-added gift and
// whether they removed it by hand. Without this the sync below re-adds the gift the moment
// the remove button empties the line, which reads as a broken cart. Session-scoped on
// purpose: a dismissal lasts the visit, a new visit offers the gift again.
var promoCampaignFreeGiftMemoryFlags = {};

function promoCampaignFreeGiftFlagKey(name, giftProductId) {
  return 'promoCampaignFreeGift:' + name + ':' + (giftProductId || 'none');
}

function getPromoCampaignFreeGiftFlag(name, giftProductId) {
  var key = promoCampaignFreeGiftFlagKey(name, giftProductId);
  if (promoCampaignFreeGiftMemoryFlags[key] !== undefined) return promoCampaignFreeGiftMemoryFlags[key];
  try {
    return window.sessionStorage.getItem(key) === '1';
  } catch (error) {
    return false;
  }
}

function setPromoCampaignFreeGiftFlag(name, giftProductId, value) {
  var key = promoCampaignFreeGiftFlagKey(name, giftProductId);
  promoCampaignFreeGiftMemoryFlags[key] = value === true;
  try {
    if (value) {
      window.sessionStorage.setItem(key, '1');
    } else {
      window.sessionStorage.removeItem(key);
    }
  } catch (error) {
    // sessionStorage unavailable (private mode, quota) — the in-memory flag still covers this page.
  }
}

// Cart line items from /cart.js carry no tags, so tag-based triggers need a per-product
// lookup. Cached by handle for the life of the page: a cart update re-runs the sync, and
// refetching the same handful of products every time would be pure waste.
var promoCampaignProductTagCache = {};

async function getPromoCampaignProductTags(handle) {
  if (!handle) return [];
  if (promoCampaignProductTagCache[handle]) return promoCampaignProductTagCache[handle];

  var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  try {
    var response = await fetch(root.replace(/\/$/, '') + '/products/' + handle + '.js');
    if (!response.ok) throw new Error('Promo campaign product tag lookup failed');
    var product = await response.json();
    promoCampaignProductTagCache[handle] = (product.tags || []).map(function (tag) {
      return String(tag).trim().toLowerCase();
    });
  } catch (error) {
    // Cache the miss too, so an unpublished or renamed handle is not refetched on every cart update.
    promoCampaignProductTagCache[handle] = [];
  }
  return promoCampaignProductTagCache[handle];
}

async function isPromoCampaignFreeGiftParent(item, promoSettings) {
  if (!item || isPromoCampaignFreeGiftItem(item, promoSettings)) return false;
  // Picker match first: it is free, so the tag lookup only runs for products not already listed.
  if (promoSettings.parentProductIds.includes(String(item.product_id))) return true;
  if (!promoSettings.parentTags.length) return false;

  var tags = await getPromoCampaignProductTags(item.handle);
  return promoSettings.parentTags.some(function (tag) { return tags.includes(tag); });
}

function getPromoCampaignFreeGiftSectionsToRender() {
  var sections = [];
  if (document.querySelector('cart-drawer')) {
    sections.push({ section: 'cart-drawer', id: 'CartDrawer', selector: '#CartDrawer' });
    sections.push({ section: 'cart-icon-bubble', id: 'cart-icon-bubble', selector: '.shopify-section' });
  }

  var mainCartItems = document.getElementById('main-cart-items');
  if (mainCartItems && mainCartItems.dataset.id) {
    sections.push({ section: mainCartItems.dataset.id, id: 'main-cart-items', selector: '.js-contents' });
  }

  var mainCartFooter = document.getElementById('main-cart-footer');
  if (mainCartFooter && mainCartFooter.dataset.id) {
    sections.push({ section: mainCartFooter.dataset.id, id: 'main-cart-footer', selector: '.js-contents2' });
  }

  var mainCartHeader = document.getElementById('main-cart-header');
  if (mainCartHeader && mainCartHeader.dataset.id) {
    sections.push({ section: mainCartHeader.dataset.id, id: 'main-cart-header', selector: '.header-contents' });
  }

  return sections;
}

function getPromoCampaignSectionInnerHTML(html, selector) {
  var parsed = new DOMParser().parseFromString(html, 'text/html');
  var target = parsed.querySelector(selector);
  return target ? target.innerHTML : null;
}

function renderPromoCampaignFreeGiftSections(parsedState, sectionsToRender) {
  if (!parsedState || !parsedState.sections) return;

  sectionsToRender.forEach(function(section) {
    var sectionHtml = parsedState.sections[section.section];
    if (!sectionHtml) return;

    if (section.section === 'cart-drawer') {
      var drawer = document.getElementById('CartDrawer');
      var drawerHtml = getPromoCampaignSectionInnerHTML(sectionHtml, '#CartDrawer');
      if (drawer && drawerHtml != null) {
        drawer.innerHTML = drawerHtml;
      }
      return;
    }

    var container = document.getElementById(section.id);
    if (!container) return;

    if (section.section === 'cart-icon-bubble') {
      var bubbleHtml = getPromoCampaignSectionInnerHTML(sectionHtml, '.shopify-section');
      if (bubbleHtml != null) container.innerHTML = bubbleHtml;
      return;
    }

    var target = container.querySelector(section.selector) || container;
    var html = getPromoCampaignSectionInnerHTML(sectionHtml, section.selector);
    if (target && html != null) {
      target.innerHTML = html;
    }
  });

  var cartDrawer = document.querySelector('cart-drawer');
  var overlay = cartDrawer?.querySelector('#CartDrawer-Overlay');
  if (cartDrawer && overlay) {
    overlay.addEventListener('click', cartDrawer.close.bind(cartDrawer));
  }

  setTimeout(function() {
    if (window.initUpsellCarousel) window.initUpsellCarousel();
    if (window.initCartCountdownTimers) window.initCartCountdownTimers();
  }, 50);
}

function getPromoCampaignFreeGiftSettings() {
  var settings = window.cartShippingSettings;
  if (!settings) return null;
  var parentProductIds = Array.isArray(settings.promoCampaignFreeGiftParentProductIds)
    ? settings.promoCampaignFreeGiftParentProductIds
    : [settings.promoCampaignFreeGiftParentProductId];

  var parentTags = String(settings.promoCampaignFreeGiftParentTags || '')
    .split(',')
    .map(function(tag) { return tag.trim().toLowerCase(); })
    .filter(Boolean);

  return {
    enabled: !!settings.enablePromoCampaignFreeGift,
    parentProductIds: parentProductIds.filter(Boolean).map(function(productId) { return String(productId); }),
    parentTags: parentTags,
    giftProductId: settings.promoCampaignFreeGiftProductId ? String(settings.promoCampaignFreeGiftProductId) : null,
    giftVariantId: settings.promoCampaignFreeGiftVariantId ? Number(settings.promoCampaignFreeGiftVariantId) : null
  };
}

function isPromoCampaignFreeGiftItem(item, promoSettings) {
  if (!item || !item.properties || item.properties._promo_campaign_free_gift !== 'true') return false;
  if (!promoSettings || !promoSettings.giftProductId) return true;
  return String(item.product_id) === promoSettings.giftProductId;
}

async function mutatePromoCampaignFreeGift(url, body) {
  var sectionsToRender = getPromoCampaignFreeGiftSectionsToRender();
  if (sectionsToRender.length) {
    body.sections = sectionsToRender.map(function(section) { return section.section; });
    body.sections_url = window.location.pathname;
  }

  var response = await fetch(url, {
    method: 'POST',
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(body)
  });

  if (!response.ok) {
    throw new Error('Promo campaign free gift cart mutation failed');
  }

  var parsedState = await response.json();
  renderPromoCampaignFreeGiftSections(parsedState, sectionsToRender);

  var cart = await fetch('/cart.js').then(function(r) { return r.json(); });
  var drawer = document.querySelector('cart-drawer');
  var drawerItems = document.querySelector('cart-drawer-items');
  var cartItems = document.querySelector('cart-items');
  if (drawer) drawer.classList.toggle('is-empty', cart.item_count === 0);
  if (drawerItems) drawerItems.classList.toggle('is-empty', cart.item_count === 0);
  if (cartItems) cartItems.classList.toggle('is-empty', cart.item_count === 0);
  if (typeof updateVerraKidsGiftMessage === 'function') updateVerraKidsGiftMessage(cart);

  cart._upsellsSynced = true;
  document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
}

async function syncPromoCampaignFreeGift(cart) {
  if (promoCampaignFreeGiftSyncing || !cart || !isValidCartItems(cart.items)) return;

  var promoSettings = getPromoCampaignFreeGiftSettings();
  var items = cart.items || [];
  var autoGiftItems = items.filter(function(item) {
    return isPromoCampaignFreeGiftItem(item, promoSettings);
  });

  var hasTriggerConfig = promoSettings && (promoSettings.parentProductIds.length > 0 || promoSettings.parentTags.length > 0);
  if (!promoSettings || !promoSettings.enabled || !hasTriggerConfig || !promoSettings.giftProductId || !promoSettings.giftVariantId) {
    if (!autoGiftItems.length) return;
    promoCampaignFreeGiftSyncing = true;
    try {
      for (var disabledIndex = 0; disabledIndex < autoGiftItems.length; disabledIndex += 1) {
        await mutatePromoCampaignFreeGift('/cart/change.js', {
          id: autoGiftItems[disabledIndex].key,
          quantity: 0
        });
      }
    } catch (error) {
      console.error('syncPromoCampaignFreeGift', error);
    } finally {
      promoCampaignFreeGiftSyncing = false;
    }
    return;
  }

  var parentItem = null;
  for (var scanIndex = 0; scanIndex < items.length; scanIndex += 1) {
    if (await isPromoCampaignFreeGiftParent(items[scanIndex], promoSettings)) {
      parentItem = items[scanIndex];
      break;
    }
  }
  var parentInCart = parentItem !== null;
  var parentProductId = parentItem ? String(parentItem.product_id) : null;
  var giftInCart = items.some(function(item) {
    return String(item.product_id) === promoSettings.giftProductId;
  });

  if (!parentInCart) {
    // No trigger product left, so the gift's lifecycle is over: forget any dismissal
    // and let a future trigger product offer the gift again.
    setPromoCampaignFreeGiftFlag('seen', promoSettings.giftProductId, false);
    setPromoCampaignFreeGiftFlag('dismissed', promoSettings.giftProductId, false);
    if (!autoGiftItems.length) return;
    promoCampaignFreeGiftSyncing = true;
    try {
      for (var removeIndex = 0; removeIndex < autoGiftItems.length; removeIndex += 1) {
        await mutatePromoCampaignFreeGift('/cart/change.js', {
          id: autoGiftItems[removeIndex].key,
          quantity: 0
        });
      }
    } catch (error) {
      console.error('syncPromoCampaignFreeGift', error);
    } finally {
      promoCampaignFreeGiftSyncing = false;
    }
    return;
  }

  // A trigger product is present. If the gift was in the cart before and is gone now,
  // only the shopper can have taken it out — record that so it is not forced back in.
  if (giftInCart) {
    setPromoCampaignFreeGiftFlag('seen', promoSettings.giftProductId, true);
    setPromoCampaignFreeGiftFlag('dismissed', promoSettings.giftProductId, false);
  } else if (getPromoCampaignFreeGiftFlag('seen', promoSettings.giftProductId)) {
    setPromoCampaignFreeGiftFlag('seen', promoSettings.giftProductId, false);
    setPromoCampaignFreeGiftFlag('dismissed', promoSettings.giftProductId, true);
  }

  if (autoGiftItems.length && autoGiftItems[0].quantity !== 1) {
    promoCampaignFreeGiftSyncing = true;
    try {
      await mutatePromoCampaignFreeGift('/cart/change.js', {
        id: autoGiftItems[0].key,
        quantity: 1
      });
    } catch (error) {
      console.error('syncPromoCampaignFreeGift', error);
    } finally {
      promoCampaignFreeGiftSyncing = false;
    }
    return;
  }

  if (giftInCart) return;

  if (getPromoCampaignFreeGiftFlag('dismissed', promoSettings.giftProductId)) return;

  promoCampaignFreeGiftSyncing = true;
  try {
    await mutatePromoCampaignFreeGift('/cart/add.js', {
      items: [{
        id: promoSettings.giftVariantId,
        quantity: 1,
        properties: {
          _promo_campaign_free_gift: 'true',
          _promo_campaign_parent_product_id: parentProductId || promoSettings.parentProductIds[0]
        }
      }]
    });
    // Mark seen right away: the shopper can hit remove before any other cart event fires,
    // and that removal must be read as a dismissal rather than a missing gift.
    setPromoCampaignFreeGiftFlag('seen', promoSettings.giftProductId, true);
  } catch (error) {
    console.error('syncPromoCampaignFreeGift', error);
  } finally {
    promoCampaignFreeGiftSyncing = false;
  }
}

// Update Omega-3 upsell visibility based on cart value and product presence
function updateOmegaUpsell(cartTotalPrice, cartItems = [], cartCurrency = null) {
  const upsellContainers = document.querySelectorAll('[data-cart-upsell-omega]');
  if (!upsellContainers.length) return;

  const settings = window.cartShippingSettings;
  if (!settings || !settings.enableOmegaUpsell) {
    // Hide if settings not found or upsell disabled
    upsellContainers.forEach(container => {
      container.style.display = 'none';
    });
    return;
  }

  // Check date validity
  if (settings.enableOmegaUpsellCustomDate) {
    const dateValid = isDateValid(
      settings.omegaUpsellValidFrom,
      settings.omegaUpsellValidUntil,
      'Omega-3 Upsell'
    );
    if (!dateValid) {
      upsellContainers.forEach(container => {
        container.style.display = 'none';
      });
      return;
    }
  }

  // Use cart currency when provided (multi-currency), else meta tag, else CZK
  const currencyRaw = (typeof cartCurrency === 'string' ? cartCurrency.trim().toUpperCase() : null)
    || document.querySelector('meta[name="currency"]')?.content?.trim()
    || document.querySelector('[data-cart-progress-bar]')?.dataset?.currency?.trim()
    || 'CZK';
  const currency = currencyRaw === 'EUR' ? 'EUR' : 'CZK';
  
  // Get threshold from settings
  const threshold = currency === 'EUR' 
    ? (settings.omegaUpsellThresholdEUR || 8000) 
    : (settings.omegaUpsellThresholdCZK || 200000);
  
  // Check currency restriction from settings
  const currencyRestriction = settings.omegaCurrencyRestriction || 'czk_only';
  let shouldShowForCurrency = false;
  if (currencyRestriction === 'both') {
    shouldShowForCurrency = true;
  } else if (currencyRestriction === 'czk_only') {
    shouldShowForCurrency = currency === 'CZK';
  } else if (currencyRestriction === 'eur_only') {
    shouldShowForCurrency = currency === 'EUR';
  }
  
  // Check if omega upsell product is already in cart (check for _upsell_omega property, not product ID)
  // This allows customers to have the full-price version and still see the upsell
  const omegaUpsellItem = cartItems.find(item => {
    return item.properties && item.properties._upsell_omega === 'true';
  });
  const omegaInCart = !!omegaUpsellItem;
  
  // Calculate cart total EXCLUDING omega upsell item (to check if other items meet threshold)
  let cartTotalWithoutOmega = cartTotalPrice;
  if (omegaUpsellItem) {
    const omegaPrice = omegaUpsellItem.final_line_price || omegaUpsellItem.line_price || omegaUpsellItem.price || 0;
    cartTotalWithoutOmega = cartTotalPrice - omegaPrice;
  }
  
  const meetsThreshold = cartTotalPrice >= threshold;
  const meetsThresholdWithoutOmega = cartTotalWithoutOmega >= threshold;
  
  // Auto-remove omega upsell if cart (without omega) no longer meets threshold
  if (omegaUpsellItem && !meetsThresholdWithoutOmega) {
    const itemLine = omegaUpsellItem.line || omegaUpsellItem.index;
    const itemKey = omegaUpsellItem.key || omegaUpsellItem.variant_id;
    
    // Use /cart/change.js with the line or key
    const removeData = itemLine 
      ? { line: itemLine, quantity: 0 }
      : { id: itemKey, quantity: 0 };
    
    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(removeData)
    })
    .then(response => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.json();
    })
    .then(cart => {
      // Dispatch cart:updated event to refresh UI
      document.dispatchEvent(new CustomEvent('cart:updated', { 
        detail: cart 
      }));
      // Reload the page to show updated cart
      if (window.location.pathname.includes('/cart')) {
        setTimeout(() => window.location.reload(), 500);
      }
    })
    .catch(() => {});
    
    // Return early - the cart update will trigger this function again
    return;
  }
  
  const shouldShow = shouldShowForCurrency && meetsThreshold && !omegaInCart;

  upsellContainers.forEach(function(container) {
    var pending = _fadeTimers.get(container);
    if (pending) { clearTimeout(pending); _fadeTimers.delete(container); }

    var isHidden = container.style.display === 'none';
    if (shouldShow && isHidden) {
      container.style.display = '';
      container.style.opacity = '0';
      container.offsetHeight;
      container.style.transition = 'opacity 0.3s ease-in-out';
      container.style.opacity = '1';
    } else if (shouldShow && !isHidden) {
      container.style.transition = 'opacity 0.3s ease-in-out';
      container.style.opacity = '1';
    } else if (!shouldShow && !isHidden) {
      container.style.transition = 'opacity 0.3s ease-in-out';
      container.style.opacity = '0';
      _fadeTimers.set(container, setTimeout(function() {
        if (container.style.opacity === '0') container.style.display = 'none';
        _fadeTimers.delete(container);
      }, 300));
    }
  });
}

var _fadeTimers = new WeakMap();

function toggleUpsellContainers(containers, shouldShow) {
  containers.forEach(function(container) {
    var pending = _fadeTimers.get(container);
    if (pending) { clearTimeout(pending); _fadeTimers.delete(container); }

    var isHidden = container.style.display === 'none';
    if (shouldShow && isHidden) {
      container.style.display = '';
      container.style.opacity = '0';
      container.offsetHeight;
      container.style.transition = 'opacity 0.3s ease-in-out';
      container.style.opacity = '1';
    } else if (shouldShow && !isHidden) {
      container.style.transition = 'opacity 0.3s ease-in-out';
      container.style.opacity = '1';
    } else if (!shouldShow && !isHidden) {
      container.style.transition = 'opacity 0.3s ease-in-out';
      container.style.opacity = '0';
      _fadeTimers.set(container, setTimeout(function() {
        if (container.style.opacity === '0') container.style.display = 'none';
        _fadeTimers.delete(container);
      }, 300));
    }
  });
}

function updateProductBasedUpsell(cartItems = [], cartCurrency = null) {
  const containers = document.querySelectorAll('[data-cart-upsell-product-based="1"]');
  if (!containers.length) return;

  const settings = window.cartShippingSettings;
  if (!settings || !settings.enableProductBasedUpsell) {
    containers.forEach(el => { el.style.display = 'none'; });
    return;
  }

  const triggerIds = settings.productBasedUpsellTriggerProductIds || [];
  if (!Array.isArray(triggerIds) || triggerIds.length === 0) {
    containers.forEach(el => { el.style.display = 'none'; });
    return;
  }

  if (settings.enableProductBasedUpsellCustomDate) {
    const dateValid = isDateValid(
      settings.productBasedUpsellValidFrom,
      settings.productBasedUpsellValidUntil,
      'Product-based Upsell'
    );
    if (!dateValid) {
      containers.forEach(el => { el.style.display = 'none'; });
      return;
    }
  }

  const currency = (typeof cartCurrency === 'string' ? cartCurrency.trim().toUpperCase() : null)
    || document.querySelector('meta[name="currency"]')?.content?.trim() || 'CZK';
  const currencyCode = currency === 'EUR' ? 'EUR' : 'CZK';
  const restriction = settings.productBasedUpsellCurrencyRestriction || 'both';
  let showForCurrency = restriction === 'both' ||
    (restriction === 'czk_only' && currencyCode === 'CZK') ||
    (restriction === 'eur_only' && currencyCode === 'EUR');
  if (!showForCurrency) {
    containers.forEach(el => { el.style.display = 'none'; });
    return;
  }

  const triggerSet = new Set(triggerIds.map(id => String(id)));
  const upsellProductId = settings.productBasedUpsellProductId ? String(settings.productBasedUpsellProductId) : null;
  const cartHasTrigger = (cartItems || []).some(item => triggerSet.has(String(item.product_id)));
  const upsellInCart = upsellProductId
    ? (cartItems || []).some(item => String(item.product_id) === upsellProductId)
    : false;
  toggleUpsellContainers(containers, cartHasTrigger && !upsellInCart);
}

function updateProductBasedUpsell2(cartItems = [], cartCurrency = null) {
  const containers = document.querySelectorAll('[data-cart-upsell-product-based="2"]');
  if (!containers.length) return;

  const settings = window.cartShippingSettings;
  if (!settings || !settings.enableProductBasedUpsell2) {
    containers.forEach(el => { el.style.display = 'none'; });
    return;
  }

  const triggerIds = settings.productBasedUpsell2TriggerProductIds || [];
  if (!Array.isArray(triggerIds) || triggerIds.length === 0) {
    containers.forEach(el => { el.style.display = 'none'; });
    return;
  }

  if (settings.enableProductBasedUpsell2CustomDate) {
    const dateValid = isDateValid(
      settings.productBasedUpsell2ValidFrom,
      settings.productBasedUpsell2ValidUntil,
      'Product-based Upsell #2'
    );
    if (!dateValid) {
      containers.forEach(el => { el.style.display = 'none'; });
      return;
    }
  }

  const currency = (typeof cartCurrency === 'string' ? cartCurrency.trim().toUpperCase() : null)
    || document.querySelector('meta[name="currency"]')?.content?.trim() || 'CZK';
  const currencyCode = currency === 'EUR' ? 'EUR' : 'CZK';
  const restriction = settings.productBasedUpsell2CurrencyRestriction || 'both';
  let showForCurrency = restriction === 'both' ||
    (restriction === 'czk_only' && currencyCode === 'CZK') ||
    (restriction === 'eur_only' && currencyCode === 'EUR');
  if (!showForCurrency) {
    containers.forEach(el => { el.style.display = 'none'; });
    return;
  }

  const triggerSet = new Set(triggerIds.map(id => String(id)));
  const upsellProductId = settings.productBasedUpsell2ProductId ? String(settings.productBasedUpsell2ProductId) : null;
  const cartHasTrigger = (cartItems || []).some(item => triggerSet.has(String(item.product_id)));
  const upsellInCart = upsellProductId
    ? (cartItems || []).some(item => String(item.product_id) === upsellProductId)
    : false;
  toggleUpsellContainers(containers, cartHasTrigger && !upsellInCart);
}

function shouldShowSlot(cartItems, cartCurrency, slotSettings) {
  if (!slotSettings.enabled) return false;
  const triggerIds = slotSettings.triggerIds || [];
  if (!Array.isArray(triggerIds) || triggerIds.length === 0) return false;
  if (slotSettings.customDate) {
    if (!isDateValid(slotSettings.validFrom, slotSettings.validUntil, 'Product-based Upsell')) return false;
  }
  const currency = (typeof cartCurrency === 'string' ? cartCurrency.trim().toUpperCase() : null)
    || document.querySelector('meta[name="currency"]')?.content?.trim() || 'CZK';
  const currencyCode = currency === 'EUR' ? 'EUR' : 'CZK';
  const restriction = slotSettings.currencyRestriction || 'both';
  const showForCurrency = restriction === 'both' ||
    (restriction === 'czk_only' && currencyCode === 'CZK') ||
    (restriction === 'eur_only' && currencyCode === 'EUR');
  if (!showForCurrency) return false;
  const triggerSet = new Set(triggerIds.map(id => String(id)));
  const upsellProductId = slotSettings.productId ? String(slotSettings.productId) : null;
  const cartHasTrigger = (cartItems || []).some(item => triggerSet.has(String(item.product_id)));
  const upsellInCart = upsellProductId
    ? (cartItems || []).some(item => String(item.product_id) === upsellProductId)
    : false;
  return cartHasTrigger && !upsellInCart;
}

function updateProductBasedUpsellGroup(cartItems, cartCurrency) {
  const groups = document.querySelectorAll('[data-cart-upsell-pb-group]');
  if (!groups.length) return;

  const settings = window.cartShippingSettings;
  const slot1Show = settings ? shouldShowSlot(cartItems, cartCurrency, {
    enabled: settings.enableProductBasedUpsell,
    triggerIds: settings.productBasedUpsellTriggerProductIds,
    customDate: settings.enableProductBasedUpsellCustomDate,
    validFrom: settings.productBasedUpsellValidFrom,
    validUntil: settings.productBasedUpsellValidUntil,
    currencyRestriction: settings.productBasedUpsellCurrencyRestriction,
    productId: settings.productBasedUpsellProductId
  }) : false;
  const slot2Show = settings ? shouldShowSlot(cartItems, cartCurrency, {
    enabled: settings.enableProductBasedUpsell2,
    triggerIds: settings.productBasedUpsell2TriggerProductIds,
    customDate: settings.enableProductBasedUpsell2CustomDate,
    validFrom: settings.productBasedUpsell2ValidFrom,
    validUntil: settings.productBasedUpsell2ValidUntil,
    currencyRestriction: settings.productBasedUpsell2CurrencyRestriction,
    productId: settings.productBasedUpsell2ProductId
  }) : false;

  toggleUpsellContainers(groups, slot1Show || slot2Show);
}

/**
 * Single entry point for omega + product-based upsells (both slots + group).
 * Expects a cart payload with `items` (array). Callers must fetch /cart.js first if `items` is missing.
 */
function syncCartDependentUpsells(cart) {
  if (!cart) return;
  var items = isValidCartItems(cart.items) ? cart.items : [];
  var cartTotalPrice = items.length
    ? items.reduce(function(sum, item) { return sum + (item.final_line_price || item.line_price || 0); }, 0)
    : (typeof cart.total_price === 'number' ? cart.total_price : 0);
  var cartCurrency = normalizeCartCurrency(cart);
  try { updateFreeShippingBar(cartTotalPrice, cartCurrency); } catch(e) { console.error('updateFreeShippingBar', e); }
  try { syncPromoCampaignFreeGift(cart); } catch(e) { console.error('syncPromoCampaignFreeGift', e); }
  try { updateOmegaUpsell(cartTotalPrice, items, cartCurrency); } catch(e) { console.error('updateOmegaUpsell', e); }
  try { updateProductBasedUpsell(items, cartCurrency); } catch(e) { console.error('updateProductBasedUpsell', e); }
  try { updateProductBasedUpsell2(items, cartCurrency); } catch(e) { console.error('updateProductBasedUpsell2', e); }
  try { updateProductBasedUpsellGroup(items, cartCurrency); } catch(e) { console.error('updateProductBasedUpsellGroup', e); }
}

window.updateOmegaUpsell = updateOmegaUpsell;
window.updateProductBasedUpsell = updateProductBasedUpsell;
window.updateProductBasedUpsell2 = updateProductBasedUpsell2;
window.updateProductBasedUpsellGroup = updateProductBasedUpsellGroup;
window.syncPromoCampaignFreeGift = syncPromoCampaignFreeGift;
window.syncCartDependentUpsells = syncCartDependentUpsells;

document.addEventListener('cart:updated', function(event) {
  const detail = event.detail;
  if (!detail) return;
  if (detail._upsellsSynced) return;
  if (isValidCartItems(detail.items)) {
    syncCartDependentUpsells(detail);
  } else {
    fetch('/cart.js')
      .then(function(r) { return r.json(); })
      .then(function(cart) { syncCartDependentUpsells(cart); })
      .catch(function() {});
  }
});


customElements.define('cart-drawer-items', CartDrawerItems);
